// ShizaiDelegationTimelineCard — visual Gantt-style timeline of all active +
// upcoming approval delegations (iter_143e P2/v4).
//
// Rendered above the delegations table. Each row is one delegate assignment
// laid out on a horizontal date axis spanning today → +60 days. Colors:
//   - emerald: active now
//   - sky:     upcoming
//   - zinc:    past (only shown if it ends within the window)
//
// Backend: GET /api/shizai/delegations/timeline?days=60 already returns
// enriched flags (active_now, upcoming, past, days_until_start,
// days_active_remaining) so we just render.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarBlank, ArrowsClockwise, PaperPlaneTilt, Clock, User,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// ISO date string → Date (UTC-safe).
const parseDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const dayMs = 86400000;

export default function ShizaiDelegationTimelineCard({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/shizai/delegations/timeline?days=60`, { withCredentials: true });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'タイムライン取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const layout = useMemo(() => {
    if (!data) return null;
    const today = parseDate(data.today);
    const horizon = parseDate(data.horizon);
    const totalDays = Math.max(1, Math.round((horizon - today) / dayMs));
    const bars = (data.delegations || []).map((d) => {
      const fd = parseDate(d.from_date) || today;
      const td = parseDate(d.to_date) || horizon;
      const startClamped = fd < today ? today : fd;
      const endClamped = td > horizon ? horizon : td;
      const leftPct = Math.max(0, Math.round(((startClamped - today) / dayMs) / totalDays * 1000) / 10);
      const widthDays = Math.max(1, Math.round((endClamped - startClamped) / dayMs) + 1);
      const widthPct = Math.max(1.5, Math.round(widthDays / totalDays * 1000) / 10);
      let tone;
      if (d.active_now) tone = 'bg-emerald-500 text-white border-emerald-600';
      else if (d.upcoming) tone = 'bg-sky-100 text-sky-900 border-sky-300';
      else tone = 'bg-zinc-200 text-zinc-700 border-zinc-300';
      return { d, leftPct, widthPct, widthDays, tone };
    });
    // Week gridlines (every 7 days)
    const gridlines = [];
    for (let i = 0; i <= totalDays; i += 7) {
      const dayDate = new Date(today.getTime() + i * dayMs);
      gridlines.push({
        pct: Math.round((i / totalDays) * 1000) / 10,
        label: `${dayDate.getUTCMonth() + 1}/${dayDate.getUTCDate()}`,
      });
    }
    return { bars, gridlines, totalDays };
  }, [data]);

  const counts = data?.counts || {};

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 mb-4" data-testid="delegation-timeline-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-bold text-zinc-800 flex items-center gap-1.5">
            <CalendarBlank weight="duotone" className="text-sky-500" size={18} />
            委任タイムライン（今後60日）
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            進行中: <span className="font-semibold text-emerald-700">{counts.active_now || 0}</span>
            {' · '}予約: <span className="font-semibold text-sky-700">{counts.upcoming || 0}</span>
            {' · '}期間切れ: <span className="font-semibold text-zinc-500">{counts.past || 0}</span>
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50 flex items-center gap-1"
          data-testid="delegation-timeline-refresh"
        >
          <ArrowsClockwise size={11} className={loading ? 'animate-spin' : ''} />
          再取得
        </button>
      </div>

      {!layout || layout.bars.length === 0 ? (
        <div className="text-xs text-zinc-400 text-center py-6" data-testid="delegation-timeline-empty">
          今後60日以内の委任予定はありません。
        </div>
      ) : (
        <div className="relative">
          {/* Axis (top) */}
          <div className="relative h-4 border-b border-zinc-200">
            {layout.gridlines.map((g, i) => (
              <div
                key={i}
                className="absolute top-0 h-full text-[9px] text-zinc-400"
                style={{ left: `${g.pct}%`, transform: 'translateX(-50%)' }}
              >
                <div className="border-l border-zinc-200 h-2 w-[1px] mx-auto" />
                {g.label}
              </div>
            ))}
          </div>

          {/* Bars */}
          <div className="mt-2 space-y-1.5">
            {layout.bars.map(({ d, leftPct, widthPct, widthDays, tone }, i) => (
              <div key={i} className="grid grid-cols-[minmax(140px,180px)_1fr] items-center gap-2" data-testid={`delegation-bar-${i}`}>
                <div className="text-[11px] leading-tight">
                  <div className="font-semibold text-zinc-800 truncate flex items-center gap-1">
                    <User size={10} className="text-zinc-400" />
                    <span className="truncate" title={d.approver_email}>{d.approver_email}</span>
                  </div>
                  <div className="text-zinc-500 flex items-center gap-1">
                    <PaperPlaneTilt size={9} />
                    <span className="truncate" title={d.delegate_email}>{d.delegate_email}</span>
                  </div>
                </div>
                <div className="relative h-6 bg-zinc-50 rounded">
                  {/* Gridlines (subtle) */}
                  {layout.gridlines.map((g, j) => (
                    <div key={j} className="absolute top-0 h-full border-l border-zinc-100" style={{ left: `${g.pct}%` }} />
                  ))}
                  {/* Bar */}
                  <div
                    className={`absolute top-0.5 bottom-0.5 rounded border text-[10px] font-semibold flex items-center px-1 truncate ${tone}`}
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                    title={`${d.from_date} 〜 ${d.to_date} (${widthDays}日)${d.note ? ' · ' + d.note : ''}`}
                  >
                    <Clock size={9} className="mr-1 shrink-0" />
                    <span className="truncate">
                      {d.from_date}〜{d.to_date}
                      {d.active_now && d.days_active_remaining != null && ` (残${d.days_active_remaining}日)`}
                      {d.upcoming && d.days_until_start != null && ` (${d.days_until_start}日後)`}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
